import React, { useState, useRef, useEffect } from 'react';
import YouTube, { YouTubeEvent, YouTubePlayer } from 'react-youtube';
import { Volume2Icon } from 'lucide-react';
interface CustomVideoPlayerProps {
  videoId: string;
}
export function CustomVideoPlayer({
  videoId
}: CustomVideoPlayerProps) {
  const [isMuted, setIsMuted] = useState(true);
  const [showUnmuteOverlay, setShowUnmuteOverlay] = useState(false);
  const playerRef = useRef<YouTubePlayer | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        // Si el video sale de la pantalla (isIntersecting es false), lo pausamos
        if (!entry.isIntersecting && playerRef.current) {
          playerRef.current.pauseVideo();
        }
      });
    }, {
      threshold: 0.1
    } // Se dispara cuando menos del 10% del video es visible
    );
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);
  const onReady = (event: YouTubeEvent) => {
    playerRef.current = event.target;
    // Try to play unmuted first
    event.target.unMute();
    event.target.playVideo();
  };
  const onStateChange = (event: YouTubeEvent) => {
    // YouTube Player States:
    // -1 (unstarted), 0 (ended), 1 (playing), 2 (paused), 3 (buffering), 5 (video cued)

    // If the video is unstarted or paused shortly after trying to play unmuted,
    // it means the browser blocked autoplay with sound.
    // We fallback to muted autoplay.
    if (event.data === -1 || event.data === 2) {
      if (!showUnmuteOverlay && isMuted) {
        event.target.mute();
        event.target.playVideo();
        setShowUnmuteOverlay(true);
      }
    } else if (event.data === 1) {
      // If it's playing and it's not muted, we successfully autoplayed with sound
      if (!event.target.isMuted()) {
        setIsMuted(false);
        setShowUnmuteOverlay(false);
      }
    }
  };
  const handleUnmuteClick = () => {
    if (playerRef.current) {
      playerRef.current.unMute();
      // Reset video to start so they don't miss anything when they unmute
      playerRef.current.seekTo(0);
      setIsMuted(false);
      setShowUnmuteOverlay(false);
    }
  };
  return <div ref={containerRef} className="relative w-full overflow-hidden rounded-[14px] shadow-panel group bg-black" style={{
    aspectRatio: '1/1'
  }} data-id="element-207" data-mp="13xdaw0y15s4x">
      <div className="absolute inset-0 w-full h-full pointer-events-auto" data-id="element-208" data-mp="zxmffjxk6nu2">
        <YouTube videoId={videoId} opts={{
        width: '100%',
        height: '100%',
        playerVars: {
          autoplay: 1,
          controls: 1,
          rel: 0,
          modestbranding: 1,
          cc_load_policy: 0,
          playsinline: 1,
          loop: 1,
          playlist: videoId // Required for loop to work
        }
      }} onReady={onReady} onStateChange={onStateChange} className="absolute inset-0 w-full h-full object-cover" iframeClassName="w-full h-full border-0" style={{
        width: '100%',
        height: '100%'
      }} data-id="element-209" data-mp="1dohq91rmbf0a" />
      </div>

      {/* Unmute Overlay */}
      {showUnmuteOverlay && <div className="absolute inset-0 flex items-center justify-center bg-black/20 transition-opacity pointer-events-none" data-id="element-210" data-mp="2nrg3t4ok28x1">
          <button onClick={handleUnmuteClick} className="pointer-events-auto flex items-center gap-2 rounded-full bg-brand-green px-6 py-3 font-bold text-ink-950 shadow-lg transition-transform hover:scale-105 active:scale-95" data-id="element-211" data-mp="3fli6jmr2jjh3">
            <Volume2Icon className="h-5 w-5" data-id="element-212" data-mp="36mel4c4firzf" />
            Activar sonido
          </button>
        </div>}
    </div>;
}